import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CircularIconContainer,
  Form,
  FormField,
  FormFieldError,
  FormFieldItem,
  IconAddCandidateOutline,
  IconPlus,
} from '@/components';
import { MarkdownEditor } from '@/components/MarkdownEditor/MarkdownEditor';
import {
  useIsTemplateInterview,
  useShareToken,
} from '@/features/admin-interview';

import {
  useAddJobDescriptionMutation,
  useAddTemplateToInterviewMutation,
  useGetInterviewQuery,
} from './useAdminInterviewQueries';

interface EditJobDescriptionModalProps extends Omit<ModalProps, 'children'> {
  interviewId: string;
}

type FormInputs = {
  jobDescription: string;
  recalculate: boolean;
};

export const EditJobDescriptionModal: React.FC<
  EditJobDescriptionModalProps
> = ({ interviewId, ...modalProps }) => {
  const { onClose } = modalProps;
  const toast = useToast();
  const isTemplate = useIsTemplateInterview();
  const shareToken = useShareToken();
  const { t } = useTranslation(['common', 'candidate', 'interview']);
  const getInterviewQuery = useGetInterviewQuery(
    {
      interviewsId: interviewId!,
    },
    {
      axiosRequestConfig: {
        overrideSecurityToken: shareToken,
      },
    }
  );

  const addJobDescriptionMutation = useAddJobDescriptionMutation({
    onSuccess() {
      form.reset();
      onClose?.();
    },
    onError() {
      toast({
        title: t('candidate:inviteNewCandidate.addCandidateError'),
        status: 'error',
      });
    },
  });

  const form = useForm<FormInputs>({
    mode: 'onBlur',
    defaultValues: {
      jobDescription: '',
      recalculate: true,
    },
    values: {
      jobDescription: getInterviewQuery.data?.interview?.jobDescription ?? '',
      recalculate: true,
    },
  });

  const addTemplateToInterview = useAddTemplateToInterviewMutation();
  const createInterviewFromTemplate = async () => {
    await addTemplateToInterview.mutateAsync(interviewId);
    const url = new URL(window.location.href);
    url.searchParams.delete('isTemplate');
    window.history.replaceState({}, '', url.toString());
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (isTemplate) await createInterviewFromTemplate();
    addJobDescriptionMutation.mutateAsync({
      interviewId,
      body: {
        jobDescription: data.jobDescription,
        recalculate: data.recalculate,
      },
    });
  };

  return (
    <Modal
      {...modalProps}
      onClose={() => {
        form.reset();
        onClose?.();
      }}
    >
      <ModalOverlay />
      <ModalContent maxW="40rem" borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader justifyItems="center">
          <VStack>
            <CircularIconContainer icon={IconAddCandidateOutline} />
            <Text>{t('interview:jobDescription.title')}</Text>
            <Text fontSize="sm" fontWeight="light">
              {t('interview:jobDescription.subtitle')}
            </Text>
          </VStack>
        </ModalHeader>

        <ModalBody w="full" pb={2}>
          <Form {...form} onSubmit={onSubmit}>
            <Stack spacing={4}>
              <Stack
                p={4}
                rounded="lg"
                background="linear-gradient(96.88deg, rgba(191, 140, 255, 0.12) 20.47%, rgba(255, 46, 0, 0.0384) 146.54%);"
              >
                <Text color="gray.700" size="sm">
                  {t('interview:jobDescription.label')}
                </Text>
                <FormField
                  control={form.control}
                  name="jobDescription"
                  type="custom"
                  render={({
                    field: { value, ref, onBlur, onChange, name },
                  }) => {
                    return (
                      <FormFieldItem>
                        <Box
                          borderRadius="lg"
                          border="1px"
                          borderColor="gray.200"
                          background="white"
                          id={name}
                        >
                          <MarkdownEditor
                            placeholder={t(
                              'interview:jobDescription.placeholder'
                            )}
                            markdown={value ?? ''}
                            onBlur={onBlur}
                            ref={ref}
                            onChange={onChange}
                          />
                        </Box>
                        <FormFieldError />
                      </FormFieldItem>
                    );
                  }}
                />
                <FormField
                  name="recalculate"
                  control={form.control}
                  type="checkbox"
                  size="lg"
                >
                  <Text color="gray.900" size="sm">
                    {t('interview:jobDescription.recalculate')}
                  </Text>
                </FormField>
              </Stack>

              <Button
                type="submit"
                mb={8}
                mt={4}
                isLoading={addJobDescriptionMutation.isPending}
                isDisabled={!form.formState.isDirty}
                leftIcon={<IconPlus />}
                variant="@primary"
                w="full"
              >
                {t('common:actions.save')}
              </Button>
            </Stack>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
